import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  SearchAndFilter,
  FirstTimeEmptyState,
  FilteredEmptyState,
} from "../../components/shared";
import { LoadingState } from "@/components/ui/loading-state";
import { PageHeader } from "@/components/ui/page-header";
import { ProtectedRoute } from "../../components/ProtectedRoute";
import { usePermission } from "../../hooks/usePermission";
import {
  AlertTriangle,
  ClipboardList,
  Clock,
  Download,
  Eye,
  FileText,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { Button, Card } from "../../components/ui";
import { PageIntent } from "../../docs/ux/page-intents";

// Page Intent: MONITORING - no stats cards/charts, allow alert-style counters only, focus on troubleshooting
const pageIntent: PageIntent = "MONITORING";

interface LogEntry {
  id: string;
  timestamp: string;
  level: "INFO" | "WARNING" | "ERROR" | "DEBUG" | "CRITICAL";
  service: string;
  message: string;
  userId?: string;
  sessionId?: string;
  ipAddress?: string;
  userAgent?: string;
  tags: string[];
  metadata: {
    duration?: number;
    statusCode?: number;
    method?: string;
    endpoint?: string;
    responseSize?: number;
  };
}

const LogsPageContent: React.FC = () => {
  const { t } = useTranslation();
  const { can } = usePermission();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState<string>("all");
  const [selectedService, setSelectedService] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Mock data - replace with actual API calls
  useEffect(() => {
    const mockLogs: LogEntry[] = [
      {
        id: "1",
        timestamp: "2024-01-15T10:30:00Z",
        level: "INFO",
        service: "API Gateway",
        message: "User authentication successful",
        userId: "user_123",
        sessionId: "sess_456",
        ipAddress: "192.168.1.100",
        userAgent: "Mozilla/5.0...",
        tags: ["authentication", "success"],
        metadata: {
          duration: 45,
          statusCode: 200,
          method: "POST",
          endpoint: "/auth/login",
          responseSize: 1024,
        },
      },
      {
        id: "2",
        timestamp: "2024-01-15T10:29:45Z",
        level: "WARNING",
        service: "Database",
        message: "Connection pool reaching capacity",
        tags: ["database", "performance"],
        metadata: {
          duration: 1200,
          statusCode: 200,
        },
      },
      {
        id: "3",
        timestamp: "2024-01-15T10:28:30Z",
        level: "ERROR",
        service: "Payment Service",
        message: "Payment processing failed",
        userId: "user_789",
        sessionId: "sess_101",
        ipAddress: "192.168.1.101",
        tags: ["payment", "failure"],
        metadata: {
          duration: 5000,
          statusCode: 500,
          method: "POST",
          endpoint: "/payments/process",
        },
      },
      {
        id: "4",
        timestamp: "2024-01-15T10:27:15Z",
        level: "DEBUG",
        service: "Workflow Engine",
        message: "Workflow execution started",
        userId: "user_456",
        tags: ["workflow", "execution"],
        metadata: {
          duration: 150,
          statusCode: 200,
          method: "POST",
          endpoint: "/workflows/execute",
        },
      },
      {
        id: "5",
        timestamp: "2024-01-15T10:26:00Z",
        level: "CRITICAL",
        service: "Email Service",
        message: "Email service unavailable",
        tags: ["email", "critical"],
        metadata: {
          duration: 0,
          statusCode: 503,
          method: "POST",
          endpoint: "/email/send",
        },
      },
    ];

    setLogs(mockLogs);
    setLoading(false);
  }, []);

  const getLevelColor = (level: string) => {
    switch (level) {
      case "INFO":
        return "info";
      case "WARNING":
        return "warning";
      case "ERROR":
        return "failure";
      case "DEBUG":
        return "gray";
      case "CRITICAL":
        return "failure";
      default:
        return "gray";
    }
  };

  const getOverallStats = () => {
    const total = logs.length;
    const errors = logs.filter(
      l => l.level === "ERROR" || l.level === "CRITICAL",
    ).length;
    const warnings = logs.filter(l => l.level === "WARNING").length;
    const avgResponseTime =
      logs.length > 0
        ? logs.reduce((sum, l) => sum + (l.metadata.duration || 0), 0) /
          logs.length
        : 0;

    return { total, errors, warnings, avgResponseTime };
  };

  const filteredLogs = logs.filter(log => {
    const matchesLevel = selectedLevel === "all" || log.level === selectedLevel;
    const matchesService =
      selectedService === "all" || log.service === selectedService;
    const matchesSearch =
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.tags.some(tag =>
        tag.toLowerCase().includes(searchTerm.toLowerCase()),
      );

    return matchesLevel && matchesService && matchesSearch;
  });

  const stats = getOverallStats();

  if (loading) {
    return <LoadingState />;
  }

  // MONITORING pages: No stats cards, but alert-style counters are allowed
  // Show simple alert counter for errors in last hour (example)
  const errorCountLastHour = stats.errors; // In real implementation, filter by time

  const filterOptions = [
    {
      label: t("all-levels"),
      value: "all",
    },
    {
      label: t("info"),
      value: "INFO",
    },
    {
      label: t("warning"),
      value: "WARNING",
    },
    {
      label: t("error"),
      value: "ERROR",
    },
    {
      label: t("debug"),
      value: "DEBUG",
    },
    {
      label: t("critical"),
      value: "CRITICAL",
    },
  ];

  const serviceOptions = [
    {
      label: t("all-services"),
      value: "all",
    },
    {
      label: t("api-gateway"),
      value: "API Gateway",
    },
    {
      label: t("database"),
      value: "Database",
    },
    {
      label: t("payment-service"),
      value: "Payment Service",
    },
    {
      label: t("workflow-engine"),
      value: "Workflow Engine",
    },
    {
      label: t("email-service"),
      value: "Email Service",
    },
  ];

  return (
    <div className="min-h-full bg-white">
      <PageHeader
        title={t("logsAndMetrics")}
        description={t("logs-description")}
        icon={ClipboardList}
        iconBgColor="from-blue-500 to-blue-600"
        actions={
          <Button variant="primary" size="sm">
            <Download className="mr-2 h-4 w-4" />
            {t("export-logs")}
          </Button>
        }
      />

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* MONITORING: No stats cards, but alert-style counters allowed */}
        {errorCountLastHour > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-red-900">
                {errorCountLastHour}{" "}
                {errorCountLastHour === 1 ? "error" : "errors"} in last hour
              </span>
            </div>
          </div>
        )}

        <SearchAndFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={t("search-for-logs")}
          filters={[
            {
              label: t("level"),
              value: selectedLevel,
              options: filterOptions,
              onChange: setSelectedLevel,
            },
            {
              label: t("service"),
              value: selectedService,
              options: serviceOptions,
              onChange: setSelectedService,
            },
          ]}
          resultsCount={filteredLogs.length}
          totalCount={logs.length}
          resultsLabel={t("logs")}
        />

        <Card className="mb-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("timestamp")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("level")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("service")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("message")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("user-id")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("ip-address")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("duration")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          getLevelColor(log.level) === "info"
                            ? "bg-blue-100 text-blue-800"
                            : getLevelColor(log.level) === "warning"
                              ? "bg-yellow-100 text-yellow-800"
                              : getLevelColor(log.level) === "failure"
                                ? "bg-red-100 text-red-800"
                                : getLevelColor(log.level) === "gray"
                                  ? "bg-gray-100 text-gray-800"
                                  : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {log.level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {log.service}
                    </td>
                    <td className="px-6 py-4 text-sm max-w-xs">
                      <div className="truncate" title={log.message}>
                        {log.message}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.userId || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.ipAddress || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.metadata.duration
                        ? `${log.metadata.duration}ms`
                        : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {log.metadata.statusCode ? (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            log.metadata.statusCode >= 400
                              ? "bg-red-100 text-red-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {log.metadata.statusCode}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm">
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <FileText className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {filteredLogs.length === 0 &&
          // Show filtered empty state if filters are active, otherwise first-time empty state
          (searchTerm ||
          selectedLevel !== "all" ||
          selectedService !== "all" ? (
            <FilteredEmptyState
              onClearFilters={() => {
                setSearchTerm("");
                setSelectedLevel("all");
                setSelectedService("all");
              }}
              title={t("no-logs-found")}
              description={t("try-adjusting-filters")}
            />
          ) : (
            <FirstTimeEmptyState
              icon={ClipboardList}
              title={t("no-logs")}
              description={t("no-logs-description")}
            />
          ))}
      </div>
    </div>
  );
};

// Wrap with ProtectedRoute for RBAC
const LogsPage: React.FC = () => {
  return (
    <ProtectedRoute action="read" subject="analytics">
      {" "}
      {/* Logs might use analytics permission, adjust if needed */}
      <LogsPageContent />
    </ProtectedRoute>
  );
};

export default LogsPage;
